import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Server,
  Settings,
  Plus,
  Activity,
} from 'lucide-react';
import { api } from '@/api/client';
import { clsx } from 'clsx';
import type { VpsEntity, VpsStatus, PaginatedResponse } from '@pulse/shared';

const statusColors: Record<VpsStatus, string> = {
  online: 'bg-status-online',
  offline: 'bg-status-offline',
  degraded: 'bg-status-degraded',
  unknown: 'bg-status-unknown',
  maintenance: 'bg-status-maintenance',
};

export function Sidebar() {
  const location = useLocation();

  const { data: vpsData } = useQuery({
    queryKey: ['vps', 'list'],
    queryFn: () => api.get<PaginatedResponse<VpsEntity>>('/vps?limit=100'),
    refetchInterval: 30000, // Refresh every 30s
  });

  const vpsList = vpsData?.items ?? [];

  return (
    <aside className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-gray-800">
        <Link to="/" className="flex items-center gap-2">
          <Activity className="h-6 w-6 text-pulse-400" />
          <span className="text-lg font-semibold text-gray-100">Pulse</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Dashboard Section */}
        <div className="px-3 mb-4">
          <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Dashboard
          </h3>
          <Link
            to="/"
            className={clsx(
              'flex items-center gap-2 px-2 py-2 text-sm rounded-lg transition-colors',
              location.pathname === '/'
                ? 'bg-gray-800 text-gray-100'
                : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800/50'
            )}
          >
            <LayoutDashboard className="h-4 w-4" />
            Overview
          </Link>
        </div>

        {/* VPS Section */}
        <div className="px-3">
          <div className="flex items-center justify-between px-2 mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              VPS Servers
            </h3>
            <Link
              to="/vps/new"
              className="p-1 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
              title="Add VPS"
            >
              <Plus className="h-4 w-4" />
            </Link>
          </div>

          <div className="space-y-1">
            {vpsList.map((vps) => (
              <Link
                key={vps.id}
                to={`/vps/${vps.id}`}
                className={clsx(
                  'flex items-center gap-2 px-2 py-2 text-sm rounded-lg transition-colors',
                  location.pathname === `/vps/${vps.id}`
                    ? 'bg-gray-800 text-gray-100'
                    : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800/50'
                )}
              >
                <Server className="h-4 w-4 flex-shrink-0" />
                <span className="truncate flex-1">{vps.name}</span>
                <span
                  className={clsx(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    statusColors[vps.status]
                  )}
                  title={vps.status}
                />
              </Link>
            ))}

            {vpsList.length === 0 && (
              <p className="px-2 py-4 text-sm text-gray-500 text-center">
                No VPS servers yet
              </p>
            )}
          </div>
        </div>
      </nav>

      {/* Settings */}
      <div className="p-3 border-t border-gray-800">
        <Link
          to="/settings"
          className={clsx(
            'flex items-center gap-2 px-2 py-2 text-sm rounded-lg transition-colors',
            location.pathname === '/settings'
              ? 'bg-gray-800 text-gray-100'
              : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800/50'
          )}
        >
          <Settings className="h-4 w-4" />
          Settings
        </Link>
      </div>
    </aside>
  );
}
